import { useQuery } from "@tanstack/react-query";

import { UserServiceProps } from "@/types/userService.type";

import { userService } from "@/services/user.service";

interface WithAuthProps {
  limit?: number;
  enabled?: boolean;
  dealType?: string;
  userId?: string;
}

interface WithoutAuthProps {
  ids: UserServiceProps["ids"];
  dealType?: string;
  limit?: number;
  enabled?: boolean;
}

export const useShowFavoriteWithAuth = ({
  limit = 12,
  enabled = true,
  dealType,
  userId,
}: WithAuthProps) => {
  const {
    data: dataWithAuth,
    error: errorWithAuth,
    isLoading: isLoadingWithAuth,
    isFetching: isFetchingWithAuth,
    refetch: refetchWithAuth,
  } = useQuery({
    queryKey: ["showFavoriteWithAuth", userId, dealType],
    queryFn: () =>
      userService.showFavoritePropertyWithAuth({ limit, dealType, userId }),
    enabled,
  });

  return {
    dataWithAuth,
    errorWithAuth,
    isLoadingWithAuth,
    isFetchingWithAuth,
    refetchWithAuth,
  };
};

export const useShowFavoriteWithoutAuth = ({
  ids,
  dealType,
  limit = 12,
  enabled = true,
}: WithoutAuthProps) => {
  const validIds = Array.isArray(ids) && ids.length > 0;

  const {
    data: dataWithoutAuth,
    error: errorWithoutAuth,
    isLoading: isLoadingWithoutAuth,
    isFetching: isFetchingWithoutAuth,
    refetch: refetchWithoutAuth,
  } = useQuery({
    queryKey: ["showFavoriteWithoutAuth", ids, dealType],
    queryFn: () => {
      return userService.showFavoritePropertyWithoutAuth({
        ids: validIds ? ids : [],
        ...(dealType ? { dealType } : {}),
        limit,
      });
    },
    enabled: enabled && validIds,
  });

  return {
    dataWithoutAuth,
    errorWithoutAuth,
    isLoadingWithoutAuth,
    isFetchingWithoutAuth,
    refetchWithoutAuth,
  };
};

export const useShowFavoriteID = (enabled: boolean = true) => {
  const {
    data: dataFavoriteID,
    error: errorFavoriteID,
    isLoading: isLoadingFavoriteID,
    isFetching: isFetchingFavoriteID,
    refetch: refetchFavoriteID,
  } = useQuery({
    queryKey: ["showFavoriteFavoriteID"],
    queryFn: () => userService.showFavoritePropertyID(),
    enabled,
  });

  return {
    dataFavoriteID,
    errorFavoriteID,
    isLoadingFavoriteID,
    isFetchingFavoriteID,
    refetchFavoriteID,
  };
};
